import * as z from 'zod'
import {
  BaseGridCell,
  BooleanEmpty,
  BooleanIndeterminate,
  DrawArgs,
  Rectangle,
  Theme,
} from '@glideapps/glide-data-grid'

import { roundedRect } from '../draw-fns'
import { MediaPickerCell } from './assets/media-picker'
import { CELL_BUTTON_SIZE } from '../theme'
import { BlockCell } from './block-cell'
import { SvgCell } from './svg-cell'
import { MediaPreviewCell } from './assets/media-preview'

export function drawCheckbox(
  ctx: CanvasRenderingContext2D,
  theme: Theme,
  checked: boolean | BooleanEmpty | BooleanIndeterminate,
  x: number,
  y: number,
  width: number,
  height: number,
  highlighted: boolean,
  hoverX: number = -20,
  hoverY: number = -20,
  maxSize: number = 32,
  alignment: BaseGridCell['contentAlign'] = 'center',
  style: 'circle' | 'square' = 'square'
) {
  const centerY = Math.floor(y + height / 2)
  const rectBordRadius =
    style === 'circle' ? 10_000 : (theme.roundingRadius ?? 4)
  let checkBoxWidth = getSquareWidth(maxSize, height, theme.cellVerticalPadding)
  let checkBoxHalfWidth = checkBoxWidth / 2
  const posX = getSquareXPosFromAlign(
    alignment,
    x,
    width,
    theme.cellHorizontalPadding,
    checkBoxWidth
  )
  const bb = getSquareBB(posX, centerY, checkBoxWidth)
  const hovered = pointIsWithinBB(x + hoverX, y + hoverY, bb)

  switch (checked) {
    case true: {
      ctx.beginPath()
      roundedRect(
        ctx,
        posX - checkBoxWidth / 2,
        centerY - checkBoxWidth / 2,
        checkBoxWidth,
        checkBoxWidth,
        rectBordRadius
      )

      if (style === 'circle') {
        checkBoxHalfWidth *= 0.8
        checkBoxWidth *= 0.8
      }

      ctx.fillStyle = theme.accentColor ?? theme.textMedium
      ctx.globalAlpha = 1
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(
        posX - checkBoxHalfWidth + checkBoxWidth / 4.23,
        centerY - checkBoxHalfWidth + checkBoxWidth / 1.97
      )
      ctx.lineTo(
        posX - checkBoxHalfWidth + checkBoxWidth / 2.42,
        centerY - checkBoxHalfWidth + checkBoxWidth / 1.44
      )
      ctx.lineTo(
        posX - checkBoxHalfWidth + checkBoxWidth / 1.29,
        centerY - checkBoxHalfWidth + checkBoxWidth / 3.25
      )

      ctx.strokeStyle = '#FAFAFA'
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.lineWidth = 1.2
      ctx.stroke()

      ctx.beginPath()
      roundedRect(
        ctx,
        posX - checkBoxWidth / 2,
        centerY - checkBoxWidth / 2,
        checkBoxWidth,
        checkBoxWidth,
        rectBordRadius
      )

      ctx.lineWidth = 1.2
      ctx.strokeStyle = theme.accentColor
      ctx.stroke()
      break
    }

    case BooleanEmpty:
    case false: {
      ctx.beginPath()
      roundedRect(
        ctx,
        posX - checkBoxWidth / 2,
        centerY - checkBoxWidth / 2,
        checkBoxWidth,
        checkBoxWidth,
        rectBordRadius
      )

      if (style === 'circle') {
        checkBoxHalfWidth *= 0.8
        checkBoxWidth *= 0.8
      }

      ctx.fillStyle = theme.bgHeaderHovered ?? theme.textMedium
      ctx.globalAlpha = 1
      ctx.fill()

      ctx.beginPath()
      roundedRect(
        ctx,
        posX - checkBoxWidth / 2,
        centerY - checkBoxWidth / 2,
        checkBoxWidth,
        checkBoxWidth,
        rectBordRadius
      )

      ctx.lineWidth = 1.2
      ctx.strokeStyle = hovered ? '#646464' : theme.bgBubble
      ctx.stroke()
      break
    }

    case BooleanIndeterminate: {
      ctx.beginPath()
      roundedRect(
        ctx,
        posX - checkBoxWidth / 2,
        centerY - checkBoxWidth / 2,
        checkBoxWidth,
        checkBoxWidth,
        rectBordRadius
      )

      ctx.fillStyle = theme.bgHeaderHovered ?? theme.textMedium
      ctx.fill()

      if (style === 'circle') {
        checkBoxHalfWidth *= 0.8
        checkBoxWidth *= 0.8
      }

      ctx.beginPath()
      ctx.moveTo(posX - checkBoxWidth / 3, centerY)
      ctx.lineTo(posX + checkBoxWidth / 3, centerY)
      ctx.strokeStyle = theme.accentColor
      ctx.lineCap = 'round'
      ctx.lineWidth = 1.9
      ctx.stroke()
      break
    }

    default:
      assertNever(checked)
  }
}

const assertNever = (x: never): never => {
  throw new Error('Unexpected object: ' + x)
}

type BoundingBox = { x1: number; y1: number; x2: number; y2: number }
export const pointIsWithinBB = (x: number, y: number, bb: BoundingBox) =>
  bb.x1 <= x && x <= bb.x2 && bb.y1 <= y && y <= bb.y2

export const getSquareBB = (
  posX: number,
  posY: number,
  squareSideLength: number
) => ({
  x1: posX - squareSideLength / 2,
  y1: posY - squareSideLength / 2,
  x2: posX + squareSideLength / 2,
  y2: posY + squareSideLength / 2,
})

export const getSquareXPosFromAlign = (
  alignment: 'left' | 'center' | 'right',
  containerX: number,
  containerWidth: number,
  horizontalPadding: number,
  squareWidth: number
) => {
  switch (alignment) {
    case 'left':
      return Math.floor(containerX) + horizontalPadding + squareWidth / 2
    case 'center':
      return Math.floor(containerX + containerWidth / 2)
    case 'right':
      return (
        Math.floor(containerX + containerWidth) -
        horizontalPadding -
        squareWidth / 2
      )
  }
}

export const getSquareWidth = (
  maxSize: number,
  containerHeight: number,
  verticalPadding: number
) => Math.min(maxSize, containerHeight - verticalPadding * 2)

export const drawAssetActions = (
  args: DrawArgs<MediaPickerCell | MediaPreviewCell>
) => {
  const { ctx, rect, theme, hoverX, hoverY, spriteManager } = args
  const yPad = theme.cellVerticalPadding
  const centerY = Math.floor(rect.y + rect.height / 2)
  const radius = 6

  const actions = args.cell.data.actions ?? []
  // let totalActionWidth = theme.cellHorizontalPadding * 2
  actions.forEach((action, index) => {
    // totalActionWidth += action.width + 4
    const posX =
      getSquareXPosFromAlign('right', rect.x, rect.width, yPad, action.width) -
      (action.width + 4) * actions.length +
      (index + 1) * (action.width + 4)

    const bb = getSquareBB(posX, centerY, action.width)
    const hovered =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bb)
        : false

    ctx.beginPath()
    roundedRect(
      ctx,
      posX - action.width / 2,
      centerY - action.width / 2,
      action.width,
      action.width,
      radius
    )
    ctx.fillStyle = hovered ? theme.bgHeaderHovered : theme.bgHeader
    ctx.fill()

    const spriteSize = 14
    spriteManager.drawSprite(
      action.sprite,
      'normal',
      ctx,
      posX - spriteSize / 2,
      centerY - spriteSize / 2,
      spriteSize,
      theme
    )

    ctx.lineWidth = 1.2
    ctx.strokeStyle = hovered
      ? theme.bgBubble
      : (theme.headerBottomBorderColor ?? 'var(--color-dark-border-solid)')
    ctx.stroke()
  })

  // const right = getSquareXPosFromAlign(
  //   'right',
  //   rect.x,
  //   rect.width,
  //   yPad,
  //   totalActionWidth
  // )
  // const posX = right - 1
  // const posY = rect.y + 1

  // ctx.beginPath()
  // const grd = ctx.createLinearGradient(
  //   posX,
  //   posY,
  //   posX - 1,
  //   posY - 1
  //   // totalActionWidth,
  //   // rect.height - 2
  // )
  // grd.addColorStop(0, 'red')
  // grd.addColorStop(1, 'white')
  // roundedRect(ctx, posX, posY, totalActionWidth, rect.height - 2, 0)
  // ctx.fillStyle = grd
  // ctx.fill()
}

export const actionSchema = z.object({
  name: z.string(),
  width: z.number(),
  height: z.number(),
  onClick: z
    .function()
    .args(z.any(), z.function().returns(z.void()))
    .returns(z.void())
    .optional(),
  sprite: z.enum(['upload', 'symbol', 'trash', 'external']),
  icon: z.any(),
})
export type Action = Omit<z.infer<typeof actionSchema>, 'icon'> & {
  icon?: React.ReactNode
}

export function isOverAction(e: {
  readonly cell: MediaPickerCell | MediaPreviewCell
  readonly posX: number
  readonly posY: number
  readonly bounds: Rectangle
  readonly theme: Theme
}): string | undefined {
  const { posX: pointerX, posY: pointerY, bounds, theme } = e
  const { width, height, x: cellX, y: cellY } = bounds
  const yPad = theme.cellVerticalPadding
  const centerY = Math.floor(cellY + height / 2)

  const actionClicked = e.cell.data.actions.find((action, index) => {
    const posX =
      getSquareXPosFromAlign('right', cellX, width, yPad, action.width) -
      (action.width + 4) * e.cell.data.actions.length +
      (index + 1) * (action.width + 4)

    const bb = getSquareBB(posX, centerY, action.width)
    const actionWasClicked = pointIsWithinBB(
      cellX + pointerX,
      cellY + pointerY,
      bb
    )

    return actionWasClicked
  })

  if (!actionClicked) return

  return actionClicked.name
}

export function isOverEditableRegion(e: {
  readonly cell: BlockCell | SvgCell
  readonly posX: number
  readonly posY: number
  readonly bounds: Rectangle
  readonly theme: any
}): boolean {
  const { posX: pointerX, posY: pointerY, bounds, theme } = e
  const { width, height, x: cellX, y: cellY } = bounds
  const maxWidth = CELL_BUTTON_SIZE
  const cellCenterY = Math.floor(bounds.y + height / 2)
  const buttonWidth = getSquareWidth(
    maxWidth,
    height,
    theme.cellVerticalPadding
  )
  const posX = getSquareXPosFromAlign(
    'right',
    cellX,
    width,
    theme.cellHorizontalPadding,
    buttonWidth
  )
  const bb = getSquareBB(posX, cellCenterY, buttonWidth)
  const checkBoxClicked = pointIsWithinBB(
    cellX + pointerX,
    cellY + pointerY,
    bb
  )

  return checkBoxClicked
}
